import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatButtonToggle } from '@angular/material/button-toggle';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DeviceAttribute } from '../../shared/models/device-attribute.model';
import { DeviceDetailService } from '../../shared/services/device-detail.service';

@Component({
  selector: 'app-device-attributes',
  templateUrl: './device-attributes.component.html',
  styleUrls: ['./device-attributes.component.scss'],
  providers: [
    DeviceDetailService,
    { provide: 'tablePaddingHeight', useValue: 216 },
    { provide: 'itemHeight', useValue: 52 },
    { provide: 'paginations', useValue: [5, 10, 25, 50] },
  ],
})
export class DeviceAttributesComponent implements OnInit, AfterViewInit {
  protected mobile: boolean = false;

  @Input() set deviceAttributesData(value: DeviceAttribute[] | undefined) {
    this.deviceAttributes.data = value ?? [];
  }

  protected readonly deviceAttributes: MatTableDataSource<DeviceAttribute> =
    new MatTableDataSource<DeviceAttribute>();
  @ViewChild('deviceAttributesPaginator', { static: true })
  private deviceAttributesPaginator!: MatPaginator;
  @ViewChild('deviceAttributesTable', { static: true })
  private deviceAttributesSort = new MatSort();
  @ViewChild('deviceAttributesGroup', { static: true }) deviceAttributesGroup:
    | MatButtonToggle
    | undefined;

  private readonly deviceAttributesColumns: (keyof DeviceAttribute)[] = [
    'attributeNum',
    'attributeName',
    'valueDisplayText',
    'attributeType',
    'lastUpdatedTimestamp',
  ];
  protected deviceAttributesColumnsToggle: (keyof DeviceAttribute)[] = [
    'attributeName',
    'valueDisplayText',
    'lastUpdatedTimestamp',
  ];
  protected get filteredDeviceAttributesColumns(): (keyof DeviceAttribute)[] {
    return this.deviceAttributesColumns.filter((col) =>
      this.deviceAttributesColumnsToggle.includes(col)
    );
  }

  constructor(
    private breakpointObserver: BreakpointObserver,
    private deviceDetailService: DeviceDetailService
  ) {
    this.deviceAttributes.sortData = this.sortDataDeviceAttributes();
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait]).subscribe({
      next: (result) => {
        this.mobile = result.matches;
        if (result.matches) {
          this.deviceAttributesColumnsToggle = [
            'attributeName',
            'valueDisplayText',
            'lastUpdatedTimestamp',
          ];
        }
        this.calcPagination();
      },
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.calcPagination(), 100);
  }

  ngOnInit(): void {
    this.deviceAttributesPaginator.pageSizeOptions =
      this.deviceDetailService.paginations;
    this.deviceAttributes.paginator = this.deviceAttributesPaginator;
    this.deviceAttributes.sort = this.deviceAttributesSort;
  }

  private sortDataDeviceAttributes() {
    return (items: DeviceAttribute[], sort: MatSort) => {
      if (!sort.active || sort.direction === '') {
        return items;
      }
      return items.sort((a, b) => {
        const asc = sort.direction === 'asc' ? 1 : -1;
        const valA: string | number =
          a[sort.active as keyof DeviceAttribute] ?? '';
        const valB: string | number =
          b[sort.active as keyof DeviceAttribute] ?? '';
        if (sort.active === 'valueDisplayText') {
          const valANum = Number(a.attributeValueNumeric);
          const valBNum = Number(b.attributeValueNumeric);
          if (!isNaN(valANum) && !isNaN(valBNum)) {
            return (valANum - valBNum) * asc;
          }
        } else if (typeof valA === 'number' && typeof valB === 'number') {
          return (valA - valB) * asc;
        }
        return String(valA).localeCompare(String(valB)) * asc;
      });
    };
  }

  private calcPagination() {
    if (this.deviceAttributesPaginator) {
      this.deviceDetailService.calculatePagination(
        this.deviceAttributesPaginator
      );
      this.deviceAttributes.paginator = this.deviceAttributesPaginator;
    }
  }
}
